// Utility functions for tracking and data capture

/**
 * Track Facebook Pixel event
 */
export const trackPixelEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', eventName, eventData);
      console.log(`Meta Pixel Event: ${eventName}`, eventData);
    } catch (error) {
      console.error('Meta Pixel tracking error:', error);
    }
  }
};

/**
 * Track custom Facebook Pixel event
 */
export const trackCustomPixelEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('trackCustom', eventName, eventData);
      console.log(`Meta Pixel Custom Event: ${eventName}`, eventData);
    } catch (error) {
      console.error('Meta Pixel custom tracking error:', error);
    }
  }
};

/**
 * Extract URL parameters from current page
 */
export const getURLParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    campaignId: params.get('campaign_id') || params.get('utm_campaign') || '',
    adsetId: params.get('adset_id') || params.get('utm_adset') || '',
    adId: params.get('ad_id') || params.get('utm_content') || '',
    source: params.get('utm_source') || '',
    medium: params.get('utm_medium') || '',
    term: params.get('utm_term') || ''
  };
};

/**
 * Store tracking data in sessionStorage
 */
export const storeTrackingData = () => {
  const trackingData = getURLParams();
  sessionStorage.setItem('trackingData', JSON.stringify(trackingData));
  return trackingData;
};

/**
 * Retrieve tracking data from sessionStorage
 */
export const getTrackingData = () => {
  const stored = sessionStorage.getItem('trackingData');
  return stored ? JSON.parse(stored) : getURLParams();
};

/**
 * Send data to Pabbly webhook
 */
export const sendToWebhook = async (data) => {
  const webhookUrl = 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjcwNTZkMDYzNzA0MzA1MjZkNTUzMjUxMzUi_pc';
  
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`Webhook failed: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Webhook error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Build payment URL with tracking parameters
 */
export const buildPaymentURL = (baseUrl, userData, trackingData) => {
  // Extract phone number without country code for separate fields
  const fullPhone = userData.phone || '';
  let countryCode = userData.countryCode || '+91';
  let phoneNumber = fullPhone;
  
  // If phone includes country code, extract it
  if (fullPhone.startsWith('+')) {
    const match = fullPhone.match(/^(\+\d{1,4})(.+)$/);
    if (match) {
      countryCode = match[1];
      phoneNumber = match[2];
    }
  }

  const params = new URLSearchParams({
    firstName: userData.firstName || userData.name?.split(' ')[0] || '',
    lastName: userData.lastName || userData.name?.split(' ').slice(1).join(' ') || '',
    email: userData.email || '',
    phone: fullPhone, // Full phone with country code
    countryCode: countryCode, // Separate country code
    phoneNumber: phoneNumber, // Phone number without country code
    profession: userData.profession || '',
    campaign_id: trackingData.campaignId || '',
    adset_id: trackingData.adsetId || '',
    ad_id: trackingData.adId || '',
    utm_source: trackingData.source || '',
    utm_medium: trackingData.medium || '',
    utm_term: trackingData.term || ''
  });

  // Remove empty parameters
  const cleanParams = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (value) {
      cleanParams.append(key, value);
    }
  }

  return `${baseUrl}${cleanParams.toString() ? '?' + cleanParams.toString() : ''}`;
};

/**
 * Format lead data for webhook
 */
export const formatLeadData = (userData, source = 'unknown') => {
  const trackingData = getTrackingData();
  
  // Extract phone components
  const fullPhone = userData.phone || '';
  let countryCode = userData.countryCode || '+91';
  let phoneNumber = fullPhone;
  
  if (fullPhone.startsWith('+')) {
    const match = fullPhone.match(/^(\+\d{1,4})(.+)$/);
    if (match) {
      countryCode = match[1];
      phoneNumber = match[2];
    }
  }
  
  return {
    // User information
    fullName: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
    firstName: userData.firstName || userData.name?.split(' ')[0] || '',
    lastName: userData.lastName || userData.name?.split(' ').slice(1).join(' ') || '',
    email: userData.email || '',
    phone: fullPhone, // Full phone with country code
    countryCode: countryCode,
    phoneNumber: phoneNumber,
    profession: userData.profession || '',
    
    // Tracking information
    campaignId: trackingData.campaignId,
    adsetId: trackingData.adsetId,
    adId: trackingData.adId,
    utmSource: trackingData.source,
    utmMedium: trackingData.medium,
    utmTerm: trackingData.term,
    
    // Meta information
    source: source,
    timestamp: new Date().toISOString(),
    pageUrl: window.location.href,
    referrer: document.referrer || 'direct'
  };
};